/**
 * Requests — the life-ring reporters' dashboard (Bill 2026-11-04). Tiles, filters, search and the list; a row opens
 * the request's page. Your own reports by default; "All pilot reports" (shown when the server says canSeeAll: every
 * pilot user during the pilot, Bill 2026-10-26 ask #285) lists every Retail report at summary level only: no one
 * else's words or details ever reach this screen (pcb-retail-api cuts them out).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiError } from '@retail-ops/api-client';
import { Banner, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { RequestRow, RequestsResponse } from './pilotApi';
import {
  EMPTY_REQUEST_FILTER,
  STAGE_OPTIONS,
  STAGE_TONE,
  filterRequests,
  requestCategories,
  requestKpis,
  shortDay,
  type KpiFilter,
  type RequestFilter,
} from './pilotModel';

const SCOPE_KEY = 'rop.pilot.requests.scope';

function readScope(): 'mine' | 'all' {
  try { return sessionStorage.getItem(SCOPE_KEY) === 'all' ? 'all' : 'mine'; } catch { return 'mine'; }
}

export function RequestsTab(): JSX.Element {
  const client = useApiClient();
  const navigate = useNavigate();
  const [scope, setScopeState] = useState<'mine' | 'all'>(readScope);
  const [data, setData] = useState<RequestsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [f, setF] = useState<RequestFilter>(EMPTY_REQUEST_FILTER);

  const setScope = (s: 'mine' | 'all'): void => {
    setScopeState(s);
    try { sessionStorage.setItem(SCOPE_KEY, s); } catch { /* a per-viewer convenience only */ }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      try {
        const r = await client.http.get<RequestsResponse>(`/v3/rop/my-requests?scope=${scope}`);
        if (!cancelled) setData(r);
      } catch (e: unknown) {
        // All taken away since the switch was shown: back to their own list.
        if (!cancelled && scope === 'all' && ApiError.isApiError(e) && e.httpStatus === 403) { setScope('mine'); return; }
        if (!cancelled) setErr(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client, scope]);

  const rows = data?.items ?? [];
  const kpis = useMemo(() => requestKpis(rows, !!data?.complianceKnown), [rows, data?.complianceKnown]);
  const shown = useMemo(() => filterRequests(rows, f), [rows, f]);
  const categories = useMemo(() => requestCategories(rows), [rows]);
  const unavailable = data != null && !data.available;
  const filtered = f.stage !== 'any' || f.category || f.from || f.to || f.q || f.tile;

  const tile = (id: Exclude<KpiFilter, null>, label: string, value: number | null, tone?: 'warn' | 'ok' | 'err', title?: string): JSX.Element => (
    <button type="button" className={`pilot-kpi${tone ? ` pilot-kpi--${tone}` : ''}${f.tile === id ? ' is-on' : ''}`}
      aria-pressed={f.tile === id} disabled={value === null} title={title}
      onClick={() => setF((x) => ({ ...x, tile: x.tile === id ? null : id }))}>
      <span className="pilot-kpi__value">{value ?? '—'}</span>
      <span className="pilot-kpi__label">{label}</span>
    </button>
  );

  return (
    <section aria-label="Requests">
      <div className="pilot-switch">
        {data?.canSeeAll ? (
          <div className="home-card__scope tg-switch" role="tablist" aria-label="Whose reports">
            <button type="button" role="tab" aria-selected={scope === 'mine'} className={`tg-switch__btn${scope === 'mine' ? ' is-on' : ''}`}
              onClick={() => setScope('mine')}>My reports</button>
            <button type="button" role="tab" aria-selected={scope === 'all'} className={`tg-switch__btn${scope === 'all' ? ' is-on' : ''}`}
              onClick={() => setScope('all')}>All pilot reports</button>
          </div>
        ) : <span className="pilot-muted">Your reports</span>}
        <span className="pilot-muted">
          {scope === 'all' ? 'Everyone’s Retail reports: summary only.' : 'Reports you sent with the life ring, and requests made for you.'}
        </span>
      </div>

      {err && <Banner tone="err" title="Couldn't load the requests">{err}</Banner>}
      {unavailable && <Banner tone="info">{data?.error ?? "Your requests can't be shown right now."}</Banner>}

      {!unavailable && (
        <div className="pilot-kpis" aria-label="Totals">
          {tile('open', 'Open', kpis.open)}
          {tile('working', 'Being worked', kpis.beingWorked)}
          {tile('live', 'Live', kpis.live, 'ok')}
          {tile('checked', 'Live and checked', kpis.checked, 'ok')}
          {tile('compliance', 'Waiting on compliance review', kpis.awaitingCompliance, 'warn',
            kpis.awaitingCompliance === null ? 'Not available yet: the Innovation Hub is not sharing compliance reviews with this app.' : undefined)}
          {tile('mine-week', 'Mine this week', kpis.mineThisWeek)}
        </div>
      )}

      <div className="pilot-filters" role="search">
        <label className="pilot-field pilot-field--grow">
          Search
          <input type="search" value={f.q} placeholder="CHG number or words in the summary"
            onChange={(e) => setF((x) => ({ ...x, q: e.target.value }))} />
        </label>
        <label className="pilot-field">
          Status
          <select value={f.stage} onChange={(e) => setF((x) => ({ ...x, stage: e.target.value as RequestFilter['stage'] }))}>
            {STAGE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </label>
        <label className="pilot-field">
          Category
          <select value={f.category} onChange={(e) => setF((x) => ({ ...x, category: e.target.value }))}>
            <option value="">Any category</option>
            {categories.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
        </label>
        <label className="pilot-field">
          Reported from
          <input type="date" value={f.from} onChange={(e) => setF((x) => ({ ...x, from: e.target.value }))} />
        </label>
        <label className="pilot-field">
          to
          <input type="date" value={f.to} onChange={(e) => setF((x) => ({ ...x, to: e.target.value }))} />
        </label>
        {filtered && (
          <button type="button" className="pilot-back" onClick={() => setF(EMPTY_REQUEST_FILTER)}>Clear filters</button>
        )}
      </div>

      {loading && !data && <div className="pilot-empty">Loading…</div>}

      {!unavailable && data && (
        <div className="pilot-list" aria-live="polite">
          {shown.length === 0 && (
            <div className="pilot-empty">
              {rows.length === 0
                ? scope === 'mine'
                  ? 'Nothing here yet. Reports you send with the life ring, and requests made for you, show up here.'
                  : 'No Retail reports yet.'
                : 'Nothing matches these filters.'}
            </div>
          )}
          {shown.map((r: RequestRow) => (
            <button key={r.key} type="button" className="pilot-row" onClick={() => navigate(`/pilot/requests/${encodeURIComponent(r.key)}`)}>
              <span className="pilot-row__key">{r.key}</span>
              <span className="pilot-row__main">
                <span className="pilot-row__title">{r.summary}</span>
                <span className="pilot-row__meta" style={{ display: 'block' }}>
                  Reported {shortDay(r.requestedAt)}
                  {scope === 'all' && ` by ${r.mine ? 'you' : r.requestedByName}`}
                  {r.category ? ` · ${r.category.charAt(0).toUpperCase()}${r.category.slice(1)}` : ''}
                  {r.fixedAt ? ` · Live ${shortDay(r.fixedAt)}` : ''}
                </span>
              </span>
              <span className="pilot-row__pills">
                {r.awaitingCompliance && <Pill variant="warn">Compliance review</Pill>}
                <Pill variant={STAGE_TONE[r.stage] ?? 'muted'}>{r.stageLabel}</Pill>
              </span>
            </button>
          ))}
        </div>
      )}
      {data && !unavailable && rows.length > 0 && (
        <p className="pilot-muted" style={{ marginTop: 8 }}>Showing {shown.length} of {rows.length}.</p>
      )}
    </section>
  );
}
